import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import dynamic from 'next/dynamic';

import clsx from 'clsx';

import useCaptionsHashtags from '@/utils/hooks/swr/useCaptionsHashtags';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { deleteApi } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';

// Lib
import { CAPTION_TYPE, HASHTAG_LIST_TYPE, TYPES } from '@/lib/captionsHashtagLists/types';
import PropTypes from 'prop-types';

// Endpoints
import { CAPTION } from '@/lib/routes/apiEndpoints';

import Button from '@/components/base/Button';
// Components
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import LoadMoreButton from '@/components/composite/Button/LoadMoreButton';
import CustomPopover from '@/components/composite/Popover/CustomPopover';
const ConfirmationModal = dynamic(() => import('@/components/composite/Modals/ConfirmationModal'));

import Item from './Item';
const UpsertCaptionModal = dynamic(() => import('./UpsertCaptionModal'));

const LoadingItems = () => {
    return [...Array(4).keys()].map((item) => (
        <div
            key={item}
            className="h-20 w-full animate-pulse rounded-m bg-icoGray-100 dark:bg-icoDarkMode-deepBlack"
        />
    ));
};

const PopoverContent = ({
    type,
    currentCaption,
    updateCaption,
    togglePopover,
    pendingDelete,
    setPendingDelete,
    isUpsertOpen,
    toggleUpsert,
    forcedSocialProfile
}) => {
    const { t } = useTranslation('publishing');

    const insertAndClosePopover = (content) => {
        updateCaption(currentCaption + ' ' + content);
        togglePopover();
    };

    // List of items //////////////////////////////////////////////////////////
    const { activeSocialProfile } = useActiveSocialProfile();

    const [{ isLoading, error }, setApiState] = useState({
        isLoading: false,
        error: null
    });
    const [pendingEdit, setPendingEdit] = useState(null);

    const closeUpsertModal = () => {
        setPendingEdit(null);
        toggleUpsert();
    };

    const editCaption = (caption) => {
        setPendingEdit(caption);
        toggleUpsert();
    };

    const {
        captionsHashtags,
        mutateCaptionsHashtags,
        loadMoreCaption,
        captionsHashtagsError,
        isLoadingInitialData,
        canLoadMore,
        isLoadingMore
    } = useCaptionsHashtags({
        activeSPID: (forcedSocialProfile ?? activeSocialProfile)?.id,
        limit: 5,
        params: { type }
    });

    const deleteCaption = async () => {
        setApiState({ isLoading: true, error: null });
        try {
            const endpoint = injectEndpointUrlParams(CAPTION, {
                SPID: (forcedSocialProfile ?? activeSocialProfile).id,
                CAPTIONID: pendingDelete.id
            });
            await deleteApi(endpoint);
            await mutateCaptionsHashtags((fetchedPages) => {
                return fetchedPages.map((page) => ({
                    ...page,
                    data: page.data.filter((c) => c.id !== pendingDelete.id)
                }));
            }, false);
            setApiState({ isLoading: false, error: null });
            setPendingDelete(null);
        } catch (err) {
            setApiState({ isLoading: false, error: err.message });
            setPendingDelete(null);
        }
    };

    return (
        <div className="z-10 flex min-h-[20rem] flex-col p-8">
            <p className="mb-4 flex items-center font-GSemiBold text-sm">
                <Icon icon={TYPES[type].icon} className="mr-2 text-icoBlue" />
                {t(`captions_and_lists.popover.${type}.title`)}
                {!isLoadingInitialData ? (
                    <span className="ml-1 text-icoBlue">
                        ({captionsHashtags.length}
                        {canLoadMore ? '+' : ''})
                    </span>
                ) : null}
            </p>
            {captionsHashtagsError ? (
                <InfoBanner
                    className="mb-4"
                    title={t('errors:error_occured')}
                    message={t(`errors:error_occured_fetch_${type}`)}
                />
            ) : null}
            {error ? (
                <InfoBanner className="mb-4" title={t('errors:error_occured')} message={error} />
            ) : null}
            {isLoadingInitialData ? (
                <LoadingItems />
            ) : (
                <>
                    <div
                        className={clsx(
                            'scrollbar -mr-1 mb-4 flex max-h-[13rem] flex-1 flex-col items-center gap-2 p-1 pr-2',
                            {
                                'justify-center': captionsHashtags.length === 0
                            }
                        )}>
                        {captionsHashtags.length > 0 ? (
                            captionsHashtags.map((item) => (
                                <Item
                                    key={item.id}
                                    item={item}
                                    insertAndClosePopover={insertAndClosePopover}
                                    editCaption={() => editCaption(item)}
                                    openDeleteModal={() => setPendingDelete(item)}
                                />
                            ))
                        ) : (
                            <p className="font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                                {t(`captions_and_lists.popover.${type}.no_data`)}
                            </p>
                        )}
                        {canLoadMore ? (
                            <LoadMoreButton
                                size="sm"
                                className="shrink-0"
                                loadMoreItems={loadMoreCaption}
                                isLoadingMore={isLoadingMore}
                            />
                        ) : null}
                    </div>
                    <Button
                        variant="secondary"
                        className="flex w-full items-center gap-2"
                        onClick={toggleUpsert}>
                        <Icon icon={TYPES[type].icon} />
                        {t(`captions_and_lists.popover.${type}.create_new`)}
                    </Button>
                </>
            )}

            <UpsertCaptionModal
                isOpen={isUpsertOpen}
                closeModal={closeUpsertModal}
                pendingEdit={pendingEdit}
                forcedSocialProfile={forcedSocialProfile}
                insertAndClosePopover={insertAndClosePopover}
                mutateCaptionsHashtags={mutateCaptionsHashtags}
                type={type}
            />
            <ConfirmationModal
                isOpen={Boolean(pendingDelete)}
                onSubmit={deleteCaption}
                closeModal={() => setPendingDelete(null)}
                isLoading={isLoading}
                title={t(`captions_and_lists.modal_remove_caption.${TYPES[type].deleteTitle}`)}>
                {t(`captions_and_lists.modal_remove_caption.${TYPES[type].deleteBody}`)}
            </ConfirmationModal>
        </div>
    );
};

const Content = ({
    type,
    currentCaption,
    updateCaption,
    portalContainer,
    isPopoverOpen,
    togglePopover,
    referenceElement,
    forcedSocialProfile
}) => {
    const { isOpen: isUpsertOpen, toggle: toggleUpsert } = useToggle();

    const [popperElement, setPopperElement] = useState();
    const [pendingDelete, setPendingDelete] = useState(null);

    const { styles, attributes } = usePopper(
        referenceElement,
        popperElement,
        TYPES[type].popoverOptions
    );
    if (!isPopoverOpen) return null;

    if (!portalContainer) {
        return (
            <CustomPopover
                closeVariant="lightGray"
                wrapperClassName="z-10"
                popperElement={popperElement}
                setPopperElement={setPopperElement}
                styles={styles}
                toggle={togglePopover}
                open={isPopoverOpen}
                attributes={attributes}
                hasArrow={false}
                width="w-[23rem]"
                isBorder
                disableClickOuter={isUpsertOpen || Boolean(pendingDelete)}>
                <PopoverContent
                    type={type}
                    currentCaption={currentCaption}
                    updateCaption={updateCaption}
                    togglePopover={togglePopover}
                    pendingDelete={pendingDelete}
                    setPendingDelete={setPendingDelete}
                    isUpsertOpen={isUpsertOpen}
                    toggleUpsert={toggleUpsert}
                    forcedSocialProfile={forcedSocialProfile}
                />
            </CustomPopover>
        );
    }

    return createPortal(
        <CustomPopover
            closeVariant="lightGray"
            wrapperClassName="z-10"
            popperElement={popperElement}
            setPopperElement={setPopperElement}
            styles={styles}
            toggle={togglePopover}
            open={isPopoverOpen}
            attributes={attributes}
            hasArrow={false}
            width="w-[23rem]"
            isBorder
            disableClickOuter={isUpsertOpen || Boolean(pendingDelete)}>
            <PopoverContent
                type={type}
                currentCaption={currentCaption}
                updateCaption={updateCaption}
                togglePopover={togglePopover}
                pendingDelete={pendingDelete}
                setPendingDelete={setPendingDelete}
                isUpsertOpen={isUpsertOpen}
                toggleUpsert={toggleUpsert}
                forcedSocialProfile={forcedSocialProfile}
            />
        </CustomPopover>,
        portalContainer
    );
};

const Popover = ({
    type,
    currentCaption,
    updateCaption,
    buttonTitle = '',
    portalContainer,
    forcedSocialProfile
}) => {
    // Popover ////////////////////////////////////////////////////////////////
    const { isOpen: isPopoverOpen, toggle: togglePopover } = useToggle();

    const [referenceElement, setReferenceElement] = useState();

    return (
        <div>
            <button
                onClick={togglePopover}
                ref={setReferenceElement}
                className={clsx(
                    'flex items-center text-xl hover:text-icoBlue dark:hover:text-icoBlue',
                    {
                        'text-icoBlue': isPopoverOpen,
                        'text-icoGray-500 dark:text-icoDarkMode-wolf': !isPopoverOpen
                    }
                )}
                title={buttonTitle}>
                <Icon icon={TYPES[type].icon} />
            </button>
            <Content
                type={type}
                currentCaption={currentCaption}
                updateCaption={updateCaption}
                portalContainer={portalContainer}
                isPopoverOpen={isPopoverOpen}
                togglePopover={togglePopover}
                referenceElement={referenceElement}
                forcedSocialProfile={forcedSocialProfile}
            />
        </div>
    );
};

Popover.propTypes = {
    type: PropTypes.oneOf([HASHTAG_LIST_TYPE, CAPTION_TYPE]).isRequired,
    currentCaption: PropTypes.string,
    updateCaption: PropTypes.func
};

export default Popover;
